import React, { useState } from 'react';
import {
  Button,
  Stack,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  MenuItem,
  Tooltip,
  Theme,
  useMediaQuery,
  Link,
} from '@mui/material';
import { Share, Delete, Edit, PlayCircleOutline, MoreHoriz, BugReport, Assignment } from '@mui/icons-material';
import { Row } from 'components/Row/Row';
import { KyronMenu } from 'components/KyronMenu';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { ErrorIndicator } from 'components/ErrorIndicator';
import { useFeatures } from 'components/FeaturesContext';
import { useProgressBarChannel } from 'components/BackgroundTasks/useProgressBarChannel';
import { getLessonCollectionURL } from 'components/utils/urlUtils';
import { Lesson, LessonCollection } from 'controllers/types';
import { useDeleteLessonCollection } from 'controllers/react-query/lessonCollectionHooks';
import { useUserContext } from 'components/UserContext';
import { ShareDialog } from 'components/ShareDialog';
import { ClassroomSelector } from 'components/CatalogLessonCard/ClassroomSelector';
import { ReauthenticateDialog } from 'components/ReauthenticateDialog';
import { useQueryClient } from '@tanstack/react-query';
import { useModal } from 'components/utils/ModalContext';
import { enqueueSnackbar } from 'notistack';
import { isEmpty } from 'lodash';

const useWatchLessonCreation = (lesson?: Lesson) => {
  const { skip_outline_view: skipOutlineView } = useFeatures(); // TODO(derick) remove after removing skip_lesson_outline feature flag
  const lessonQueryClient = useQueryClient();
  const [watchingLessonIds, setWatchingLessonIds] = useState<number[]>([]);
  const taskName = skipOutlineView
    ? `generate-entire-lesson-${lesson?.id}`
    : `create-and-publish-studio-lesson-${lesson?.id}`;
  const processingTask = lesson?.background_tasks?.find(t => t.name === taskName);
  const { status, message } = useProgressBarChannel(processingTask);

  if (status === 'error') {
    console.error(`Error processing lesson [${taskName}]:${processingTask?.id}\n${message}`);
  }

  if ((status === 'started' || status === null) && lesson?.id && !watchingLessonIds.includes(lesson?.id)) {
    setWatchingLessonIds(prevState => [...prevState, lesson?.id]);
  }

  if (status === 'done' && lesson?.id && watchingLessonIds.includes(lesson?.id)) {
    lessonQueryClient.invalidateQueries({ queryKey: ['/lessons'] });
    const newWatchingLessonIds = watchingLessonIds.filter(lessonId => lessonId !== lesson?.id);
    setWatchingLessonIds(newWatchingLessonIds);
  }

  return {
    isInProgress: status === 'started' || status === null, // status will be null when task is a pending activejob
    error: status === 'error' ? new Error(`Error processing lesson: ${lesson?.id}`) : null,
  };
};

export function LibraryListItem({ lessonCollection }: { lessonCollection: LessonCollection }) {
  const isMobileView = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));
  // Studio library should have only one lesson variant in the lesson collection, so will always pick the first one.
  const { skip_outline_view: skipOutlineView } = useFeatures(); // TODO(derick) remove after removing skip_lesson_outline feature flag
  const draftLesson = lessonCollection.draft_lessons?.[0];
  const hasPublishedLesson = !isEmpty(lessonCollection.lessons);
  const publishedLessonLink = hasPublishedLesson ? getLessonCollectionURL(lessonCollection) : '';
  const { isInProgress, error } = useWatchLessonCreation(draftLesson);
  const shouldDisableLessonLink = !hasPublishedLesson || isInProgress || Boolean(error);
  const shouldDisableEditLink = !skipOutlineView && isInProgress;
  const { isPlatformAdmin } = useUserContext();
  const [openShareDialog, setOpenShareDialog] = React.useState(false);
  const { openModal, closeModal } = useModal();
  const { mutateAsync: deleteLessonCollection } = useDeleteLessonCollection(
    lessonCollection ? lessonCollection.id : Number(0),
  );
  const [openAssignDialog, setOpenAssignDialog] = React.useState(false);
  const [showReauthenticateDialog, setShowReauthenticateDialog] = React.useState(false);
  const isPrivateLesson = lessonCollection.visibility === 'PRIVATE';

  function openDeleteModal() {
    const handleDelete = () => {
      deleteLessonCollection({})
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: any) => {
          if (response.code === 0) {
            enqueueSnackbar(`Deleted Lesson ${lessonCollection.name}.`);
          } else if (response.code === 1) {
            enqueueSnackbar(`Warning: Active Lesson ${lessonCollection.name} deleted.`);
          }
        })
        .catch((err: Error) => {
          console.error(err);
          enqueueSnackbar(`Error deleting lesson ${lessonCollection.name}`, { variant: 'error' });
        });
      closeModal();
    };
    openModal({
      id: 'delete-lesson',
      title: 'Delete Lesson',
      content: 'Are you sure you want to delete this lesson?',
      action: (
        <Stack direction='row' spacing={2}>
          <Button variant='outlined' onClick={closeModal}>
            Cancel
          </Button>
          <Button color='error' onClick={handleDelete}>
            Delete
          </Button>
        </Stack>
      ),
    });
  }

  const additionalActions: Array<{ iconButton: React.ReactNode; menuItem: React.ReactNode }> = [
    {
      iconButton: (
        <Tooltip title={isPrivateLesson ? 'Lesson must be public in order to share' : ''} key='lesson-share-button'>
          <span>
            <IconButton
              data-testid='lesson-share-button'
              disabled={shouldDisableLessonLink || isPrivateLesson}
              onClick={() => setOpenShareDialog(true)}
            >
              <Share />
            </IconButton>
          </span>
        </Tooltip>
      ),
      menuItem: (
        <MenuItem
          key='lesson-share-button'
          data-testid='lesson-share-button'
          disabled={shouldDisableLessonLink || isPrivateLesson}
          onClick={() => setOpenShareDialog(true)}
        >
          <ListItemIcon sx={{ margin: 0 }}>
            <Share />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
      ),
    },
    {
      iconButton: isPlatformAdmin ? (
        <IconButton
          key='lesson-edit-debug-button'
          data-testid='lesson-edit-debug-button'
          href={`/studio/internal_lesson_editor/${draftLesson?.id}/edit`}
          disabled={shouldDisableEditLink}
        >
          <BugReport />
        </IconButton>
      ) : null,
      menuItem: isPlatformAdmin ? (
        <MenuItem
          key='lesson-edit-debug-button'
          data-testid='lesson-edit-debug-button'
          component={Link}
          href={`/studio/internal_lesson_editor/${draftLesson?.id}/edit`}
          disabled={shouldDisableEditLink}
        >
          <ListItemIcon sx={{ margin: 0 }}>
            <BugReport />
          </ListItemIcon>
          <ListItemText>Debug</ListItemText>
        </MenuItem>
      ) : null,
    },
  ];

  return (
    <>
      <ListItem key={lessonCollection.id} sx={{ py: 2 }} divider>
        <Stack direction={isMobileView ? 'column' : 'row'} width='100%' gap={1} justifyContent='space-between'>
          <ListItemText sx={{ textWrap: 'balance' }}>{lessonCollection.name}</ListItemText>
          <Row gap={1} justifyContent='space-between'>
            <LoadingIndicator size={28} sx={{ mr: 1 }} loading={isInProgress} />
            <ErrorIndicator error={error} />
            <Button
              startIcon={<PlayCircleOutline />}
              data-testid='lesson-start-button'
              href={publishedLessonLink}
              target='_blank'
              disabled={shouldDisableLessonLink}
            >
              Start Lesson
            </Button>
            <Row gap={1}>
              <IconButton
                data-testid='lesson-edit-button'
                disabled={draftLesson?.use_df || shouldDisableEditLink}
                href={
                  draftLesson?.use_df
                    ? `/studio/internal_lesson_editor/${draftLesson.id}/edit`
                    : `/studio/${draftLesson?.id}`
                }
              >
                <Edit />
              </IconButton>
              {!isMobileView ? additionalActions.map(action => action.iconButton) : null}
              <KyronMenu menuIcon={<MoreHoriz />}>
                <MenuItem data-testid='lesson-delete-button' onClick={openDeleteModal} disabled={draftLesson?.use_df}>
                  <ListItemIcon sx={{ margin: 0 }}>
                    <Delete />
                  </ListItemIcon>
                  <ListItemText>Delete Lesson</ListItemText>
                </MenuItem>
                <MenuItem
                  data-testid='assign-lesson-button'
                  onClick={() => setOpenAssignDialog(true)}
                  disabled={shouldDisableLessonLink}
                >
                  <ListItemIcon sx={{ margin: 0 }}>
                    <Assignment />
                  </ListItemIcon>
                  <ListItemText>Assign Lesson</ListItemText>
                </MenuItem>
                {isMobileView ? additionalActions.map(action => action.menuItem) : null}
              </KyronMenu>
            </Row>
          </Row>
        </Stack>
      </ListItem>
      <ShareDialog
        open={openShareDialog}
        onClose={() => setOpenShareDialog(false)}
        quote={`Check out this lesson I created on Kyron Learning, ${lessonCollection.name}!`}
        emailMessage={`I made a lesson on Kyron Learning called ${lessonCollection.name}, and I wanted to share it with you. Click the link to check it out!`}
        link={publishedLessonLink}
        eventResource={{ lesson_collection_id: lessonCollection.id, lesson_name: lessonCollection.name }}
      />
      {hasPublishedLesson && (
        <>
          <ClassroomSelector
            open={openAssignDialog}
            close={() => setOpenAssignDialog(false)}
            lessonCollection={lessonCollection}
            reauthenticate={() => {
              setShowReauthenticateDialog(true);
              setOpenAssignDialog(false);
            }}
          />
          <ReauthenticateDialog open={showReauthenticateDialog} onClose={() => setShowReauthenticateDialog(false)} />
        </>
      )}
    </>
  );
}
